import type { MaybeRefOrGetter } from 'vue'

interface UseProductCardLinkOptions {
  link: MaybeRefOrGetter<string>
  qid: MaybeRefOrGetter<string | null | undefined>
  openInNewTab: MaybeRefOrGetter<boolean>
  beforeNavigationCallback: MaybeRefOrGetter<(() => void) | undefined>
  beforeSameTabLocationAssign: MaybeRefOrGetter<(() => void) | undefined>
}

/**
 * Link + click handling shared by the V2 product cards. Same behaviour as
 * `ProductCard.vue`: search tracking (qid, Insights/GA4 callback) and list
 * scroll restore depend on it.
 */
export const useProductCardLink = ({
  link,
  qid,
  openInNewTab,
  beforeNavigationCallback,
  beforeSameTabLocationAssign,
}: UseProductCardLinkOptions) => {
  const localePath = useLocalePath()

  // qid is appended only after mount so the SSR (CDN-cached) HTML stays the
  // same for every search.
  const isMounted = ref(false)
  onMounted(() => {
    isMounted.value = true
  })

  const fullLink = computed(() => {
    const base = toValue(link)
    const queryId = toValue(qid)
    if (isMounted.value && queryId) {
      const separator = base?.includes('?') ? '&' : '?'
      return `${base}${separator}qid=${queryId}`
    }
    return base
  })

  const href = computed(() => localePath(fullLink.value))

  const handleClick = (event: Event) => {
    const callback = toValue(beforeNavigationCallback)
    // Without a callback the plain link navigates on its own.
    if (!callback) return

    event.preventDefault()

    // window.open() must run synchronously inside the click, or popup
    // blockers stop it.
    if (toValue(openInNewTab)) {
      const openedWindow = window.open(href.value, '_blank')
      if (openedWindow) {
        callback()
        return
      }
      console.warn('Popup blocked, falling back to same tab navigation')
    }

    // The callback sends tracking events, so it runs exactly once and before
    // the same-tab navigation unloads the page.
    callback()
    toValue(beforeSameTabLocationAssign)?.()
    window.location.href = href.value
  }

  return { href, handleClick }
}
